/**
 * PATH: packages/job-hub/src/jobs/qa/qaApi.ts
 * FILE_ID: JOBHUB-QA-API-001
 * PURPOSE: Stable public API for the Jobs Hub QA domain.
 * WHY: Consumers keep one import path while endpoint clients split by responsibility.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import { createQaHoldPointApi } from './qaHoldPointApi'
import { createQaInspectionApi } from './qaInspectionApi'
import { createQaPlanApi } from './qaPlanApi'
import { createQaReportsApi } from './qaReportsApi'
import { createQaTemplatesApi } from './qaTemplatesApi'
import type { QaApi } from './qaTypes'

export * from './archivedStructureTemplates'
export * from './qaApiHelpers'
export * from './qaHoldPoints'
export * from './qaCrossDoorLinks'
export * from './qaInspectionStatusLabel'
export * from './qaTemplateTypes'
export * from './qaTypes'

export function createQaApi(client: JobHubHttpClient, orgId?: number): QaApi {
  const headers = orgId != null ? { 'X-Organisation-Id': String(orgId) } : undefined
  return {
    ...createQaInspectionApi(client, headers),
    ...createQaHoldPointApi(client, headers),
    ...createQaPlanApi(client, headers),
    ...createQaReportsApi(client, headers),
    ...createQaTemplatesApi(client, headers),
  }
}

/**
 * Legacy source-pin compatibility. Runtime implementations live in the domain
 * clients above; these signatures and endpoint contracts remain public:
 * renameInspection(inspectionId: string, name: string)
 * client.patch(`/api/qa/inspections/${inspectionId}/`
 * deleteInspection(inspectionId: string)
 * client.delete(`/api/qa/inspections/${inspectionId}/`
 * export function qaScopeName
 * QA Scope ${scopeNumber}
 * addItem(areaId: string) /api/qa/areas/${areaId}/add-item/
 * updateItem(templateItemId: string) /api/qa/template-items/${templateItemId}/
 * deleteItem(itemInstanceId: string)
 * reorderItems(order: /api/qa/item-instances/reorder/
 * addTable(inspectionId: string) /api/qa/areas/add-table/
 * aiGenerateItems(areaId: string, products?: string[]) /api/qa/areas/${areaId}/ai-generate-items/
 * setReading(itemId: string, capturedValue: string) captured_value?: string
 * /api/qa/item-instances/${itemId}/set-reading/
 * setLineChecks(itemId: string, kind: 'requirement' | 'criteria', indexes: number[])
 * /api/qa/item-instances/${itemId}/set-line-checks/
 * /api/qa/reports/generate/ /api/qa/reports/${reportId}/download/
 */
